import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { TrendingUp, Percent, IndianRupee, ArrowRight, Eye, CheckCircle2, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_OPTIONS = [
  { key: "basic_pay", label: "Basic Pay" },
  { key: "da", label: "DA" },
  { key: "hra", label: "HRA" },
  { key: "conveyance", label: "Conveyance" },
  { key: "other_allowances", label: "Other Allowances" },
  { key: "daily_rate", label: "Daily Rate" },
  { key: "monthly_salary", label: "Monthly Salary (Employee Master)" },
];

/**
 * SalaryIncrementDialog — unified dialog for per-employee & bulk hikes.
 * Props:
 *   open, onOpenChange
 *   mode: "single" | "bulk"
 *   employee: employee object (for single mode)
 *   allEmployees: array (for bulk mode)
 *   onApplied: callback after successful apply
 */
const SalaryIncrementDialog = ({ open, onOpenChange, mode, employee, allEmployees = [], onApplied }) => {
  const [hikeType, setHikeType] = useState("percent");
  const [value, setValue] = useState("");
  const [fields, setFields] = useState({ basic_pay: true, da: true, hra: true, conveyance: true, other_allowances: true, daily_rate: false, monthly_salary: false });
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split("T")[0]);
  const [note, setNote] = useState("");
  const [selectedEmps, setSelectedEmps] = useState([]); // for bulk
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setHikeType("percent");
      setValue("");
      setFields({ basic_pay: true, da: true, hra: true, conveyance: true, other_allowances: true, daily_rate: false, monthly_salary: false });
      setEffectiveDate(new Date().toISOString().split("T")[0]);
      setNote("");
      setPreview(null);
      if (mode === "bulk") {
        // Default select all Employed
        const employed = allEmployees.filter(e => e.status === "Employed").map(e => e.name);
        setSelectedEmps(employed);
      }
    }
  }, [open, mode, allEmployees]);

  const toggleField = (k) => setFields({ ...fields, [k]: !fields[k] });
  const selectedFields = Object.keys(fields).filter(k => fields[k]);

  const filteredEmployees = allEmployees.filter(e => {
    if (categoryFilter === "cpi") return e.category === "CPI" && e.status === "Employed";
    if (categoryFilter === "tw") return e.category === "TW" && e.status === "Employed";
    if (categoryFilter === "employed") return e.status === "Employed";
    return true;
  });

  const toggleEmp = (name) => {
    setSelectedEmps(selectedEmps.includes(name)
      ? selectedEmps.filter(n => n !== name)
      : [...selectedEmps, name]);
  };
  const selectAllFiltered = () => setSelectedEmps([...new Set([...selectedEmps, ...filteredEmployees.map(e => e.name)])]);
  const clearSelection = () => setSelectedEmps([]);

  const getEmployeesPayload = () => {
    if (mode === "single" && employee) return [employee.name];
    return selectedEmps;
  };

  const handlePreview = async () => {
    if (!value || parseFloat(value) === 0) { toast.error("Enter a non-zero value"); return; }
    if (selectedFields.length === 0) { toast.error("Select at least one field"); return; }
    const payload = { employees: getEmployeesPayload(), fields: selectedFields, hike_type: hikeType, value: parseFloat(value) };
    try {
      setLoading(true);
      const res = await axios.post(`${API}/hr/increments/preview`, payload);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!value || parseFloat(value) === 0) { toast.error("Enter a non-zero value"); return; }
    if (selectedFields.length === 0) { toast.error("Select at least one field"); return; }
    const emps = getEmployeesPayload();
    if (mode === "bulk" && emps.length === 0) { toast.error("Select at least one employee"); return; }
    try {
      setApplying(true);
      await axios.post(`${API}/hr/increments`, {
        employees: emps,
        fields: selectedFields,
        hike_type: hikeType,
        value: parseFloat(value),
        effective_date: effectiveDate,
        note,
      });
      toast.success(`Increment applied to ${emps.length} employee(s)`);
      onOpenChange(false);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally {
      setApplying(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-amber-500/40 text-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="increment-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-300 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {mode === "single" ? `Salary Increment — ${employee?.name}` : "Bulk Salary Increment"}
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            Apply a yearly hike. Increment history is saved and can be reverted later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Hike type + value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Hike Type</Label>
              <Select value={hikeType} onValueChange={setHikeType}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="select-hike-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="percent" className="text-white"><Percent className="h-3 w-3 inline mr-1" /> Percentage</SelectItem>
                  <SelectItem value="fixed" className="text-white"><IndianRupee className="h-3 w-3 inline mr-1" /> Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Value {hikeType === "percent" ? "(%)" : "(₹)"}</Label>
              <Input
                type="number" step="0.01" value={value}
                onChange={e => { setValue(e.target.value); setPreview(null); }}
                placeholder={hikeType === "percent" ? "10" : "2000"}
                className="bg-gray-800 border-gray-600 text-white mt-1"
                data-testid="input-hike-value"
              />
            </div>
          </div>

          {/* Fields to apply */}
          <div>
            <Label className="text-gray-300">Apply To Fields</Label>
            <div className="grid grid-cols-2 gap-1 mt-2 bg-gray-800/40 rounded-lg p-3 border border-gray-700">
              {FIELD_OPTIONS.map(f => (
                <label key={f.key} className={cn("flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs hover:bg-gray-800/80", fields[f.key] && "bg-amber-500/10")}>
                  <input
                    type="checkbox" checked={fields[f.key]}
                    onChange={() => { toggleField(f.key); setPreview(null); }}
                    className="w-4 h-4 accent-amber-500"
                    data-testid={`field-${f.key}`}
                  />
                  <span className="text-gray-200">{f.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Effective date + note */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Effective Date</Label>
              <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-effective-date" />
            </div>
            <div>
              <Label className="text-gray-300">Note (optional)</Label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="FY 26-27 annual hike" className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-note" />
            </div>
          </div>

          {/* Bulk: employee selector */}
          {mode === "bulk" && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-gray-300">Employees ({selectedEmps.length} selected)</Label>
                <div className="flex items-center gap-2">
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem value="all" className="text-white">All</SelectItem>
                      <SelectItem value="employed" className="text-white">Only Employed</SelectItem>
                      <SelectItem value="cpi" className="text-white">CPI (Employed)</SelectItem>
                      <SelectItem value="tw" className="text-white">TW (Employed)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={selectAllFiltered} className="h-7 text-xs text-amber-300">Select visible</Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection} className="h-7 text-xs text-gray-400">Clear</Button>
                </div>
              </div>
              <div className="bg-gray-800/40 rounded-lg p-3 border border-gray-700 max-h-44 overflow-y-auto mt-2">
                <div className="grid grid-cols-2 gap-1" data-testid="bulk-employee-list">
                  {filteredEmployees.map(e => (
                    <label key={e.id} className={cn("flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs hover:bg-gray-800/80", selectedEmps.includes(e.name) && "bg-amber-500/10")}>
                      <input
                        type="checkbox" checked={selectedEmps.includes(e.name)}
                        onChange={() => toggleEmp(e.name)}
                        className="w-3.5 h-3.5 accent-amber-500"
                      />
                      <span className="text-gray-200 truncate">{e.name}</span>
                      <Badge variant="outline" className={cn("text-[9px] ml-auto", e.category === "CPI" ? "bg-blue-500/15 text-blue-300 border-blue-500/40" : "bg-purple-500/15 text-purple-300 border-purple-500/40")}>
                        {e.category}
                      </Badge>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Preview results */}
          {preview && (
            <div className="bg-black/40 rounded-lg p-3 border border-amber-500/30" data-testid="preview-results">
              <div className="flex items-center gap-2 mb-2 text-amber-300 text-sm font-semibold">
                <Eye className="h-4 w-4" /> Preview — {preview.count} employee(s)
              </div>
              <div className="max-h-56 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-800 hover:bg-transparent">
                      <TableHead className="text-gray-500 text-[10px] uppercase h-7">Employee</TableHead>
                      <TableHead className="text-gray-500 text-[10px] uppercase h-7">Field</TableHead>
                      <TableHead className="text-right text-gray-500 text-[10px] uppercase h-7">Old</TableHead>
                      <TableHead className="text-gray-500 text-[10px] uppercase h-7 w-8"></TableHead>
                      <TableHead className="text-right text-gray-500 text-[10px] uppercase h-7">New</TableHead>
                      <TableHead className="text-right text-gray-500 text-[10px] uppercase h-7">Δ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.employees.flatMap(emp =>
                      Object.entries(emp.changes).map(([field, chg]) => (
                        <TableRow key={`${emp.employee_name}_${field}`} className="border-gray-800 hover:bg-gray-800/30">
                          <TableCell className="text-gray-300 text-xs py-1.5">{emp.employee_name}</TableCell>
                          <TableCell className="text-gray-400 text-xs py-1.5">{FIELD_OPTIONS.find(f => f.key === field)?.label || field}</TableCell>
                          <TableCell className="text-right text-gray-400 font-mono text-xs py-1.5">₹{fmt(chg.old)}</TableCell>
                          <TableCell className="py-1.5 text-amber-400"><ArrowRight className="h-3 w-3" /></TableCell>
                          <TableCell className="text-right text-emerald-300 font-mono text-xs py-1.5 font-semibold">₹{fmt(chg.new)}</TableCell>
                          <TableCell className="text-right text-amber-300 font-mono text-xs py-1.5">+₹{fmt(chg.delta)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-600 text-gray-300" data-testid="btn-cancel-increment">Cancel</Button>
          <Button onClick={handlePreview} disabled={loading} className="bg-amber-700/60 hover:bg-amber-700 text-amber-100" data-testid="btn-preview">
            <Eye className="h-4 w-4 mr-1" /> {loading ? "..." : "Preview"}
          </Button>
          <Button onClick={handleApply} disabled={applying || !preview} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-apply-increment">
            <CheckCircle2 className="h-4 w-4 mr-1" /> {applying ? "Applying..." : "Apply & Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SalaryIncrementDialog;
